import { useEffect, useRef, useMemo } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '@/modules/routes/contexts/MapboxMapContext'
import { WaypointResponse } from '@/shared/api/routes'
import { TripResponse } from '@/shared/api/types'
import { organizeWaypointsByDay } from '../utils/itineraryHelpers'

// Color palette for different days (matches ItineraryMap)
const DAY_COLORS = [
  '#5C8DC5', // Primary blue - Day 1
  '#909EAE', // Neutral gray - Day 2
  '#AD9E90', // Accent 1 - Day 3
  '#736F60', // Accent 2 - Day 4
  '#8B9DC3', // Light blue - Day 5
  '#A89B8D', // Light brown - Day 6
  '#9B9B9B', // Gray - Day 7
]

export interface UseItineraryBuilderWaypointMarkersProps {
  waypoints?: WaypointResponse[]
  trip?: TripResponse | null
  selectedTripId?: string | null
  selectedWaypoint?: WaypointResponse | null
  onWaypointClick?: (waypoint: WaypointResponse) => void
}

/**
 * Hook for displaying day-based numbered markers for itinerary builder waypoints
 * Shows numbered badges (1, 2, 3...) for each day, restarting at 1 for each day
 * Numbers align with the drag-and-drop panel order
 * Only displays markers when selectedTripId is set and matches trip
 */
export function useItineraryBuilderWaypointMarkers({ 
  waypoints = [], 
  trip,
  selectedTripId,
  selectedWaypoint,
  onWaypointClick
}: UseItineraryBuilderWaypointMarkersProps) {
  
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const markerElementsRef = useRef<Map<string, { wrapper: HTMLElement; inner: HTMLElement; clickHandler?: (e: MouseEvent) => void }>>(new Map())
  const waypointIdsRef = useRef<string>('')
  const itineraryWaypointIdsRef = useRef<string>('')
  const markersMapRef = useRef<Map<string, { marker: mapboxgl.Marker; waypoint: WaypointResponse }>>(new Map())
  const onWaypointClickRef = useRef(onWaypointClick)
  
  // Store callback in ref to avoid recreating markers when callback changes
  useEffect(() => {
    onWaypointClickRef.current = onWaypointClick
  }, [onWaypointClick])

  // Create stable waypoint IDs for comparison
  const currentWaypointIds = useMemo(() => {
    if (!waypoints.length) return ''
    const sorted = [...waypoints].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    return sorted.map(wp => wp.id).join(',')
  }, [waypoints])

  // Organize waypoints by day for day-based numbering
  // Filter out GPX imported waypoints without trip_id (they show book icon instead)
  const itineraryDays = useMemo(() => {
    if (!trip || !selectedTripId || trip.id !== selectedTripId || !waypoints.length) {
      return []
    }

    // Filter to only waypoints that belong to this trip (have trip_id)
    // Exclude GPX imported waypoints without trip_id (they show book icon)
    const tripWaypoints = waypoints.filter(
      wp => wp.trip_id === selectedTripId && !(wp.source === 'gpx_import' && !wp.trip_id)
    )

    const days = organizeWaypointsByDay(tripWaypoints, trip.start_date, trip.end_date)
    
    return days
  }, [waypoints, trip, selectedTripId])

  // Create stable waypoint IDs from itineraryDays for marker comparison
  // This ensures we detect changes in the actual waypoints that should be displayed as markers
  const itineraryWaypointIds = useMemo(() => {
    if (!itineraryDays.length) return ''
    const allWaypointIds = itineraryDays.flatMap(day => 
      day.waypoints.map(wp => wp.id)
    )
    return allWaypointIds.sort().join(',')
  }, [itineraryDays])

  // Helper to update marker style
  // Uses a wrapper with fixed dimensions to prevent Mapbox from recalculating position
  const updateMarkerStyle = (wrapper: HTMLElement, inner: HTMLElement, isSelected: boolean) => {
    // Wrapper always maintains fixed size - this prevents Mapbox from repositioning
    // Inner element scales within the wrapper
    const innerSize = isSelected ? '40px' : '32px'
    inner.style.width = innerSize
    inner.style.height = innerSize
    inner.style.fontSize = isSelected ? '14px' : '12px'
    inner.style.border = isSelected ? '4px solid white' : '2px solid white'
    inner.style.boxShadow = isSelected 
      ? '0 4px 8px rgba(0,0,0,0.4), 0 0 0 2px rgba(92, 141, 197, 0.3)' 
      : '0 2px 4px rgba(0,0,0,0.3)'
    
    if (isSelected) {
      inner.style.animation = 'waypoint-pulse 2s ease-in-out infinite'
    } else {
      inner.style.animation = ''
    }
  }

  useEffect(() => {
    // Only show markers when viewing an itinerary with a valid trip
    if (!selectedTripId || !trip || trip.id !== selectedTripId) {
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    if (!map.current || !isLoaded || !styleLoaded) {
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    // If no itinerary days to show, clear existing markers
    if (itineraryDays.length === 0) {
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      markerElementsRef.current.clear()
      markersMapRef.current.clear()
      waypointIdsRef.current = ''
      return
    }

    // Check if waypoints actually changed
    const waypointsChanged = waypointIdsRef.current !== currentWaypointIds
    // Also check if itinerary waypoints changed (the actual waypoints that should be displayed as markers)
    const itineraryWaypointsChanged = itineraryWaypointIdsRef.current !== itineraryWaypointIds

    // Always clean up existing markers before creating new ones
    // This ensures stale markers from deleted waypoints are removed
    const cleanupMarkers = () => {
      // Remove event listeners and DOM elements before removing markers
      markerElementsRef.current.forEach((elements, waypointId) => {
        try {
          // Remove click event listener if it exists
          if (elements.clickHandler) {
            elements.inner.removeEventListener('click', elements.clickHandler)
          }
        } catch (error) {
          // Ignore cleanup errors
        }
      })
      
      // Remove all markers from map
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (error) {
          // Marker may already be removed, ignore error
        }
      })
      
      markersRef.current = []
      markerElementsRef.current.clear()
      markersMapRef.current.clear()
    }

    // Recreate markers if waypoints changed OR if itinerary waypoints changed
    // This ensures markers appear immediately when a new waypoint is added
    // itineraryWaypointsChanged catches cases where a new waypoint is added but might not
    // be detected by waypointsChanged (e.g., if order_index hasn't been set yet)
    if (waypointsChanged || itineraryWaypointsChanged) {
      // Clear existing markers
      cleanupMarkers()

      // Create numbered markers for each waypoint, grouped by day
      itineraryDays.forEach((day) => {
        day.waypoints.forEach((waypoint, indexInDay) => {
          const lat = waypoint.latitude
          const lng = waypoint.longitude

          if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
            return
          }

          // Number restarts at 1 for each day (matches drag-and-drop panel)
          const number = indexInDay + 1
          const dayColor = DAY_COLORS[(day.day - 1) % DAY_COLORS.length]
          const isSelected = selectedWaypoint?.id === waypoint.id

          // Create wrapper element with FIXED dimensions
          // This prevents Mapbox from recalculating position when inner element size changes
          const wrapperEl = document.createElement('div')
          wrapperEl.style.width = '48px' // Fixed size - large enough for selected state
          wrapperEl.style.height = '48px'
          wrapperEl.style.display = 'flex'
          wrapperEl.style.alignItems = 'center'
          wrapperEl.style.justifyContent = 'center'
          wrapperEl.style.pointerEvents = 'none' // Allow clicks to pass through wrapper

          // Create inner marker element that can change size
          const markerEl = document.createElement('div')
          markerEl.className = 'itinerary-waypoint-marker'
          markerEl.style.borderRadius = '50%'
          markerEl.style.backgroundColor = dayColor
          markerEl.style.color = 'white'
          markerEl.style.display = 'flex'
          markerEl.style.alignItems = 'center'
          markerEl.style.justifyContent = 'center'
          markerEl.style.fontWeight = 'bold'
          markerEl.textContent = number.toString()
          markerEl.style.cursor = 'pointer'
          markerEl.style.transition = 'all 0.2s ease-in-out'
          markerEl.style.pointerEvents = 'auto' // Re-enable pointer events on inner element
          
          // Create click handler function (store reference for cleanup)
          const clickHandler = (e: MouseEvent) => {
            e.stopPropagation()
            e.preventDefault()
            if (onWaypointClickRef.current) {
              onWaypointClickRef.current(waypoint)
            }
          }
          
          // Add click handler for waypoint zoom
          markerEl.addEventListener('click', clickHandler)
          
          wrapperEl.appendChild(markerEl)
          
          // Set initial style
          updateMarkerStyle(wrapperEl, markerEl, isSelected)

          const marker = new mapboxgl.Marker({
            element: wrapperEl, // Use wrapper for Mapbox positioning
            anchor: 'center'
          })
            .setLngLat([lng, lat])
            .addTo(map.current!)

          markersRef.current.push(marker)
          // Store both wrapper and inner element for style updates, plus click handler for cleanup
          markerElementsRef.current.set(waypoint.id, { wrapper: wrapperEl, inner: markerEl, clickHandler })
          markersMapRef.current.set(waypoint.id, { marker, waypoint })
        })
      })

      waypointIdsRef.current = currentWaypointIds
      itineraryWaypointIdsRef.current = itineraryWaypointIds
    }

    return () => {
      // Cleanup: Remove all markers when component unmounts or dependencies change
      // First remove event listeners to prevent memory leaks
      markerElementsRef.current.forEach((elements) => {
        try {
          if (elements.clickHandler) {
            elements.inner.removeEventListener('click', elements.clickHandler)
          }
        } catch (error) {
          // Ignore cleanup errors
        }
      })
      
      // Then remove markers from map
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (error) {
          // Marker may already be removed, ignore error
        }
      })
      
      markersRef.current = []
      markerElementsRef.current.clear()
      markersMapRef.current.clear()
      waypointIdsRef.current = ''
      itineraryWaypointIdsRef.current = ''
    }
  }, [map, isLoaded, styleLoaded, selectedTripId, trip, currentWaypointIds, itineraryDays, itineraryWaypointIds])

  // Separate effect for updating selection styles only
  // This prevents marker recreation when only selection changes
  useEffect(() => {
    if (markerElementsRef.current.size === 0) {
      return
    }

    const selectedId = selectedWaypoint?.id || null
    markerElementsRef.current.forEach((elements, waypointId) => {
      const isSelected = waypointId === selectedId
      updateMarkerStyle(elements.wrapper, elements.inner, isSelected)
    })
  }, [selectedWaypoint])

  return { itineraryDays }
}

